import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Shield, Users } from 'lucide-react';

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '500+', label: 'Students Trained' },
    { number: '8', label: 'Programs Offered' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Discipline',
      description: 'Building mental fortitude and self-control through rigorous training.',
    },
    {
      icon: Shield,
      title: 'Defense',
      description: 'Practical self-defense skills for real-world protection.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'A supportive family of fighters growing together.',
    },
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#050505]"
      data-testid="about-section"
    >
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#DC2626]/5 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
              About Us
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6 leading-tight">
              Forging Champions<br />
              <span className="text-[#DC2626]">Since Day One</span>
            </h2>
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed mb-6">
              SDM School of Defense & Martial Arts is Mysuru's premier destination for 
              professional martial arts training. Founded with a mission to build discipline, 
              strength, and self-defense skills, we've transformed hundreds of students into 
              confident, capable individuals.
            </p>
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed mb-8">
              Our world-class facility features state-of-the-art equipment, a professional 
              octagon, and dedicated training areas for each discipline. Whether you're a 
              beginner looking to get fit or an aspiring fighter, our expert coaches will 
              guide you every step of the way.
            </p>

            {/* Values */}
            <div className="grid grid-cols-3 gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#121212] border border-[#262626] mb-3">
                    <value.icon size={24} className="text-[#DC2626]" />
                  </div>
                  <h4 className="font-['Oswald'] text-white text-sm uppercase tracking-wide">
                    {value.title}
                  </h4>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Image & Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative">
              <div className="absolute -inset-4 bg-[#DC2626]/20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1737381508529-a110d717d5a7?q=85&w=800&auto=format&fit=crop"
                alt="MMA Training at SDM"
                className="relative w-full h-[400px] md:h-[500px] object-cover border border-[#262626]"
              />
              {/* Overlay corner accent */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-4 border-b-4 border-[#DC2626]" />
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="grid grid-cols-3 gap-4 mt-8 p-6 bg-[#121212] border border-[#262626]"
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-['Oswald'] text-2xl md:text-3xl font-bold text-[#DC2626]">
                    {stat.number}
                  </p>
                  <p className="text-neutral-500 text-xs uppercase tracking-wider mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
